/**
 * Panel de Control para Laboratorios (Dashboard Lab).
 * Proporciona herramientas integradas para la gestión de pacientes, registro de casos clínicos,
 * visualización de estadísticas epidemiológicas y exportación de datos anonimizados.
 */

import React, { useEffect, useState, useCallback } from "react";
import { Apiurl } from "../../shared/services/apirest";
import axios from "axios";
import LocationService from "../../shared/services/LocationService";
import LabLayout from "./LabLayout";
import "bootstrap-icons/font/bootstrap-icons.css";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import NotificationsPanel from "../../shared/components/NotificationsPanel";

// --- Funciones de Utilidad ---
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return { headers: { Authorization: `Bearer ${token}` } };
};

// --- Componentes Reutilizables de Interfaz ---
const ParasiteIcon = ({ color, width = 30, height = 20 }) => (
  <svg viewBox="0 0 100 60" width={width} height={height} xmlns="http://www.w3.org/2000/svg" style={{ overflow: 'visible', verticalAlign: 'middle', marginRight: '8px' }}>
    <path d="M10,30 Q25,5 40,30 T70,30 T90,10" fill="none" stroke="white" strokeWidth="14" strokeLinecap="round" />
    <path d="M10,30 Q25,5 40,30 T70,30 T90,10" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" />
    <circle cx="10" cy="30" r="14" fill={color} stroke="white" strokeWidth="2" />
    <circle cx="6" cy="26" r="3" fill="white" />
    <circle cx="14" cy="26" r="3" fill="white" />
    <circle cx="6" cy="26" r="1" fill="black" />
    <circle cx="14" cy="26" r="1" fill="black" />
  </svg>
);

/**
 * Verifica la presencia de un parásito en una lista (formato string o array).
 */
const checkParasitePresence = (pList, query) => {
  if (!pList || !query) return false;
  try {
    let list = pList;
    if (typeof list === 'string') {
      if (list.trim().startsWith('[') && list.trim().endsWith(']')) {
        list = JSON.parse(list.replace(/'/g, '"'));
      } else {
        list = [list];
      }
    }
    if (!Array.isArray(list)) return String(list).includes(query);
    return list.some(p => String(p).includes(query));
  } catch (e) {
    return String(pList).includes(query);
  }
};

// --- Sub-Componentes de Vista ---

/**
 * Componente: Resumen Ejecutivo.
 * Presenta KPIs y gráficas de distribución de riesgo.
 */
const ExecutiveSummary = ({ setView }) => {
  const [stats, setStats] = useState({ total: 0, active: 0, highRisk: 0, riskData: [] });
  const [loading, setLoading] = useState(true);
  const nombreLab = (localStorage.getItem("nombre_laboratorio") || "Usr. Laboratorio").trim();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${Apiurl}casos/`, getAuthHeader());
        const data = res.data;

        // Cálculo de estadísticas
        const total = data.length;
        // Se asumen casos activos aquellos registrados en los últimos 30 días
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const active = data.filter(c => new Date(c.fecha_registro) >= thirtyDaysAgo).length;

        const highRiskCount = data.filter(c => c.nivel_peligrosidad === 'ALTO').length;
        const highRisk = total > 0 ? ((highRiskCount / total) * 100).toFixed(1) : 0;

        // Datos para la gráfica
        const riskCounts = { ALTO: 0, MEDIO: 0, BAJO: 0 };
        data.forEach(c => {
          const risk = c.nivel_peligrosidad || 'BAJO';
          if (riskCounts[risk] !== undefined) riskCounts[risk]++;
        });

        const riskData = [
          { name: 'Alto', cantidad: riskCounts.ALTO, fill: '#EF5350' },
          { name: 'Medio', cantidad: riskCounts.MEDIO, fill: '#FFA726' },
          { name: 'Bajo', cantidad: riskCounts.BAJO, fill: '#66BB6A' },
        ];

        setStats({ total, active, highRisk, riskData });
      } catch (err) {
        console.error("Error loading stats:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="container-fluid px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-dark m-0">Situación Epidemiológica Actual</h4>
          <small className="text-muted">Indicadores consolidados del laboratorio</small>
        </div>
        <div className="text-end d-flex align-items-center gap-3">
          <div>
            <small className="d-block text-secondary">Bienvenido,</small>
            <span className="fw-bold text-primary">{nombreLab}</span>
          </div>
        </div>
      </div>
      {
        loading ? (
          <div className="text-center py-5" > <div className="spinner-border text-primary"></div></div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body d-flex justify-content-between">
                    <div>
                      <p className="text-muted small text-uppercase fw-bold mb-1">Total Procesados</p>
                      <h3 className="fw-bold text-dark m-0">{stats.total}</h3>
                    </div>
                    <div className="p-2 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                      <i className="bi bi-clipboard-data text-primary fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body d-flex justify-content-between">
                    <div>
                      <p className="text-muted small text-uppercase fw-bold mb-1">Casos (30 días)</p>
                      <h3 className="fw-bold text-dark m-0">{stats.active}</h3>
                    </div>
                    <div className="p-2 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                      <i className="bi bi-calendar-event text-warning fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body d-flex justify-content-between">
                    <div>
                      <p className="text-muted small text-uppercase fw-bold mb-1">% Riesgo Alto</p>
                      <h3 className="fw-bold text-dark m-0">{stats.highRisk}%</h3>
                    </div>
                    <div className="p-2 d-flex align-items-center justify-content-center" style={{ width: 50, height: 50 }}>
                      <i className="bi bi-exclamation-triangle text-danger fs-4"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Charts & Actions */}
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                <div className="card h-100">
                  <div className="card-header bg-light pt-3 pb-2">
                    <h6 className="fw-bold text-dark">Distribución por nivel de riesgo</h6>
                  </div>
                  <div className="card-body" style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.riskData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" width={80} />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="cantidad" fill="#8884d8" name="Casos" radius={[0, 4, 4, 0]}>
                          {stats.riskData.map((entry, index) => (
                            <cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="col-12 col-lg-4">
                <div className="card bg-white h-100 text-center p-4 border">
                  <h5 className="mb-4 text-dark">Accesos Rápidos</h5>
                  <button className="btn btn-primary w-100 mb-3 fw-bold py-2" onClick={() => setView("registrarPaciente")}>
                    <i className="bi bi-person-plus-fill me-2"></i> Nuevo Paciente
                  </button>
                  <button className="btn btn-outline-primary w-100 fw-bold py-2" onClick={() => setView("buscarPaciente")}>
                    <i className="bi bi-clipboard2-pulse-fill me-2"></i> Reportar Caso
                  </button>
                  <div className="mt-4 pt-3 border-top text-start">
                    <small className="d-block text-muted mb-1"><i className="bi bi-info-circle me-1"></i> Recuerde validar las muestras antes de registrar.</small>
                  </div>
                </div>
              </div>
            </div>
          </>
        )
      }
    </div >
  );
};

/**
 * Componente: Mis Pacientes.
 * Listado detallado de pacientes registrados por el laboratorio.
 */
const MisPacientes = ({ onEdit, showToast, onViewHistory }) => {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchPacientes();
  }, []);

  const fetchPacientes = async () => {
    try {
      const res = await axios.get(`${Apiurl}casos/pacientes/`, getAuthHeader());
      setPacientes(res.data);
    } catch (err) {
      console.error("Error cargando pacientes:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${Apiurl}casos/pacientes/${id}/`, getAuthHeader());
      showToast("Paciente eliminado correctamente", "success");
      setPacientes(prev => prev.filter(p => p.id !== id));
      setShowDeleteConfirm(null);
    } catch (err) {
      showToast("Error al eliminar paciente. Puede tener casos asociados.", "danger");
      setShowDeleteConfirm(null);
    }
  };

  const filtered = pacientes.filter(p =>
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.documento.includes(searchTerm)
  );

  return (
    <div>
      <div className="toolbar">
        <div className="search-wrapper">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nombre o ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-container position-relative">
        <div className="table-responsive">
          <table className="table admin-table align-middle mb-0">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Nombre</th>
                <th>Edad / Sexo</th>
                <th>Ubicación</th>
                <th>Teléfono</th>
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-4">Cargando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-4 text-muted">No se encontraron pacientes.</td></tr>
              ) : (
                filtered.map(p => (
                  <tr key={p.id}>
                    <td className="fw-bold text-dark">{p.documento}</td>
                    <td>{p.nombre}</td>
                    <td>{p.edad} años <span className="text-muted">({p.sexo})</span></td>
                    <td>
                      <div className="fw-bold text-secondary">{p.municipio}</div>
                      <div className="text-muted small" style={{ fontSize: '0.75rem' }}>{p.direccion}</div>
                    </td>
                    <td>{p.telefono || "---"}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-info me-2" onClick={() => onViewHistory(p)} title="Ver Historial">
                        <i className="bi bi-clock-history"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => onEdit(p)} title="Editar">
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setShowDeleteConfirm(p)} title="Eliminar">
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="position-absolute top-0 start-0 w-100 h-100 bg-white d-flex align-items-center justify-content-center" style={{ zIndex: 10 }}>
            <div className="card border-danger" style={{ maxWidth: '400px' }}>
              <div className="card-body text-center">
                <i className="bi bi-exclamation-circle text-danger fs-1 mb-2"></i>
                <h5 className="fw-bold">¿Eliminar Paciente?</h5>
                <p className="text-muted small">
                  ¿Estás seguro de eliminar a <strong>{showDeleteConfirm.nombre}</strong>?
                  Esta acción no se puede deshacer.
                </p>
                <div className="d-flex justify-content-center gap-2 mt-4">
                  <button className="btn btn-light" onClick={() => setShowDeleteConfirm(null)}>Cancelar</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(showDeleteConfirm.id)}>Sí, Eliminar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Componente: Mis Casos.
 * Gestión de registros epidemiológicos y resultados parasitológicos.
 */
const MisCasos = ({ onEdit, showToast, initialSearch = "" }) => {
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [parasito, setParasito] = useState("");
  const [resultado, setResultado] = useState("POSITIVO");

  useEffect(() => {
    fetchCasos();
  }, [initialSearch]);

  const fetchCasos = async () => {
    try {
      let url = `${Apiurl}casos/`;
      if (initialSearch) {
        url += `?documento=${initialSearch}`;
      }
      const res = await axios.get(url, getAuthHeader());
      setCasos(res.data);
    } catch (err) {
      console.error("Error cargando casos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${Apiurl}casos/${id}/`, getAuthHeader());
      showToast("Caso eliminado correctamente", "success");
      setCasos(prev => prev.filter(c => c.id !== id));
      setShowDeleteConfirm(null);
    } catch (err) {
      showToast("Error al eliminar caso.", "danger");
      setShowDeleteConfirm(null);
    }
  };

  const handleResultadoChange = (e) => {
    if (!parasito) {
      showToast("⚠️ Debe seleccionar un parásito primero para filtrar por resultado.", "warning");
      return;
    }
    setResultado(e.target.value);
  };

  const filtered = casos.filter(c => {
    const matchesSearch = c.paciente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.paciente_documento?.includes(searchTerm) ||
      (c.paciente_municipio && c.paciente_municipio.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearch) return false;

    if (parasito) {
      const isPos = checkParasitePresence(c.parasitos_detectados, parasito);
      if (resultado === "POSITIVO") return isPos;
      return !isPos;
    }

    return true;
  });

  return (
    <div>
      <div className="toolbar d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div className="d-flex flex-wrap gap-2 flex-grow-1">
          <div className="search-wrapper" style={{ minWidth: '250px' }}>
            <i className="bi bi-search"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por paciente o municipio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="d-flex gap-2">
            <select className="form-select form-select-sm" style={{ width: '150px' }} value={parasito} onChange={e => setParasito(e.target.value)}>
              <option value="">Todos los Parásitos</option>
              <option value="Ascaris">Ascaris</option>
              <option value="Trichuris">Trichuris</option>
              <option value="Uncinarias">Uncinarias</option>
            </select>

            <select className="form-select form-select-sm" style={{ width: '120px' }} value={resultado} onChange={handleResultadoChange}>
              <option value="POSITIVO">POSITIVO</option>
              <option value="NEGATIVO">NEGATIVO</option>
            </select>
          </div>
        </div>
        {initialSearch && (
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-info p-2">
              <i className="bi bi-filter-circle me-1"></i>
              Filtrando Historial: <strong>{initialSearch}</strong>
            </span>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => {
              setSearchTerm("");
              // La lógica para recargar todo se activaría si vaciamos el filtro
            }}>Limpiar</button>
          </div>
        )}
      </div>

      <div className="table-container position-relative">
        <div className="table-responsive">
          <table className="table admin-table align-middle mb-0">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Paciente</th>
                <th>Parásitos Detectados</th>
                <th>Nivel Riesgo</th>
                <th>Municipio</th>
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-4">Cargando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-4 text-muted">No se encontraron casos registrados.</td></tr>
              ) : (
                filtered.map(c => (
                  <tr key={c.id}>
                    <td>{c.fecha_registro}</td>
                    <td className="fw-bold">{c.paciente_nombre || `Paciente #${c.paciente}`}</td>
                    <td>
                      {c.parasitos_detectados && c.parasitos_detectados.length > 0 ? (
                        c.parasitos_detectados.map((p, i) => (
                          <span key={i} className="badge bg-secondary me-1">{p}</span>
                        ))
                      ) : (
                        <span className="text-muted small">Negativo / Ninguno</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge ${c.nivel_peligrosidad === 'ALTO' ? 'bg-danger' :
                        c.nivel_peligrosidad === 'MEDIO' ? 'bg-warning text-dark' : 'bg-success'
                        }`}>
                        {c.nivel_peligrosidad || "BAJO"}
                      </span>
                    </td>
                    <td>{c.paciente_municipio || "---"}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => onEdit(c)} title="Editar">
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => setShowDeleteConfirm(c)} title="Eliminar">
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="position-absolute top-0 start-0 w-100 h-100 bg-white d-flex align-items-center justify-content-center" style={{ zIndex: 10 }}>
            <div className="card border-danger" style={{ maxWidth: '400px' }}>
              <div className="card-body text-center">
                <i className="bi bi-exclamation-circle text-danger fs-1 mb-2"></i>
                <h5 className="fw-bold">¿Eliminar Caso?</h5>
                <p className="text-muted small">
                  Importante: Esta acción eliminará el registro epidemiológico del paciente <strong>{showDeleteConfirm.paciente_nombre}</strong>.
                </p>
                <div className="d-flex justify-content-center gap-2 mt-4">
                  <button className="btn btn-light" onClick={() => setShowDeleteConfirm(null)}>Cancelar</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(showDeleteConfirm.id)}>Sí, Eliminar</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Componente: Registrar Paciente.
 * Formulario para la creación y edición de datos demográficos de pacientes.
 */
const RegistrarPaciente = ({ showToast, setView, initialData, isEditMode, onCancel, setSelectedPaciente }) => {
  // State
  const [municipio, setMunicipio] = useState(initialData?.municipio || "");
  const [zona, setZona] = useState(initialData?.zona || "");
  // Parse lugar from full address if possible or just rely on manual input if saved separately?
  // Ideally backend saves it. For backward compatibility if 'lugar' not explicit, we might parse.
  // Assuming 'lugar' is not stored separately in backend model? 
  // Wait, in previous task I sent 'zona' and 'municipio' but not 'lugar' explicit field unless I added it to model.
  // The backend might not have 'lugar' field. It likely parses 'direccion'.
  // For Edit Mode to work perfectly, we need to extract 'lugar' from 'direccion' if not available.
  // Direccion format: `${municipio}, ${zona}, ${lugar} - ...`
  // Intenta extraer el 'lugar' (barrio/vereda) desde la dirección completa si no existe campo explícito
  const extractLugar = (addr) => {
    if (!addr) return "";
    const parts = addr.split(',');
    if (parts.length >= 3) {
      const sub = parts[2].split('-');
      return sub[0].trim();
    }
    return "";
  };
  const [lugar, setLugar] = useState(initialData?.lugar || extractLugar(initialData?.direccion) || "");

  const [coords, setCoords] = useState({
    lat: initialData?.latitud || "",
    lng: initialData?.longitud || ""
  });
  const [fechaNacimiento, setFechaNacimiento] = useState(initialData?.fecha_nacimiento || "");
  const [fechaEvento, setFechaEvento] = useState(initialData?.fecha_evento || new Date().toISOString().split('T')[0]);
  const [edad, setEdad] = useState(initialData?.edad || "");

  // Locations Data
  const [locationsHierarchy, setLocationsHierarchy] = useState({});
  const [municipiosList, setMunicipiosList] = useState([]);

  useEffect(() => {
    LocationService.getHierarchy().then(data => {
      setLocationsHierarchy(data);
      setMunicipiosList(Object.keys(data).sort());
    });
  }, []);

  // Confirmation Modal State
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  // Classification Modal State
  const [showClassification, setShowClassification] = useState(false);
  const [createdPatient, setCreatedPatient] = useState(null);
  const [loadingClassification, setLoadingClassification] = useState(false);

  // Handlers
  const handleMunicipioChange = (e) => {
    const val = e.target.value;
    setMunicipio(val);

    // Auto coords from Municipality
    if (locationsHierarchy[val]) {
      let lat = "";
      let lng = "";
      // Try default logic: Urbana -> Centro, or first available
      const urb = locationsHierarchy[val]["Urbana"];
      if (urb) {
        const place = urb["Centro"] || Object.values(urb)[0];
        if (place) { lat = place.lat; lng = place.lng; }
      } else {
        const rur = locationsHierarchy[val]["Rural"];
        if (rur) {
          const place = Object.values(rur)[0];
          if (place) { lat = place.lat; lng = place.lng; }
        }
      }

      // Only update coords if we found them, otherwise leave as is or clear? 
      if (lat && lng) {
        setCoords({ lat, lng });
      }
    }
  };

  const handleFechaChange = (e) => {
    const val = e.target.value;
    setFechaNacimiento(val);
    if (val) {
      const birthDate = new Date(val);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      setEdad(age >= 0 ? age : 0);
    }
  };

  const [patientExists, setPatientExists] = useState(null);

  const handlePreSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Check if patient exists (only if not in edit mode)
    if (!isEditMode) {
      try {
        const res = await axios.get(`${Apiurl}casos/pacientes/?documento=${data.documento}`, getAuthHeader());
        const existing = res.data.find(p => p.documento === data.documento);
        if (existing) {
          setPatientExists(existing);
          setConfirmData(data);
          setShowConfirm(true);
          return;
        }
      } catch (err) {
        console.error("Error checking existing patient:", err);
      }
    }

    // Enrich data
    data.edad = edad;
    data.latitud = coords.lat;
    data.longitud = coords.lng;
    data.municipio = municipio;
    data.zona = zona;
    data.fecha_evento = fechaEvento;
    // Construir dirección completa para el backend
    data.direccion = `${municipio}, ${zona}, ${lugar} - ${data.direccion_detalles || ''}`;

    setPatientExists(null);
    setConfirmData(data);
    setShowConfirm(true);
  };

  const handleFinalSubmit = async () => {
    try {
      if (isEditMode && initialData.id) {
        await axios.put(`${Apiurl}casos/pacientes/${initialData.id}/`, confirmData, getAuthHeader());
        showToast("✅ Paciente actualizado correctamente.", "success");
        if (onCancel) onCancel();
      } else {
        const res = await axios.post(`${Apiurl}casos/pacientes/`, confirmData, getAuthHeader());
        showToast("✅ Paciente registrado correctamente.", "success");
        setCreatedPatient(res.data);
        setShowClassification(true);
      }

      setShowConfirm(false);
      setConfirmData(null);
    } catch (err) {
      console.error(err);
      let msg = isEditMode ? "Error al actualizar." : "Error al registrar.";
      if (err.response?.data) msg += " " + JSON.stringify(err.response.data);
      showToast(msg, "danger");
      setShowConfirm(false);
    }
  };

  const handleClassification = async (resultado) => {
    setLoadingClassification(true);
    try {
      await axios.patch(`${Apiurl}casos/pacientes/${createdPatient.id}/`, { resultado_inicial: resultado }, getAuthHeader());

      if (resultado === 'NEGATIVO') {
        showToast("✅ Resultado NEGATIVO guardado. Registro epidemiológico finalizado.", "success");
        if (onCancel) onCancel();
        else setView("home");
      } else {
        showToast("✅ Resultado POSITIVO detectado. Redirigiendo a Registro de Caso...", "info");
        setSelectedPaciente(createdPatient);
        setView("registrarCaso");
      }
    } catch (err) {
      console.error(err);
      showToast("Error al guardar la clasificación.", "danger");
    } finally {
      setLoadingClassification(false);
    }
  };

  return (
    <div className="table-container position-relative">
      {/* Header */}
      <div className="mb-3 pb-2 border-bottom d-flex justify-content-between">
        <div>
          <h5 className="m-0 fw-bold text-primary">{isEditMode ? "Editar Paciente" : "Información del Paciente"}</h5>
          <small className="text-muted" style={{ fontSize: '0.75rem' }}>{isEditMode ? "Actualice los datos necesarios" : "Complete todos los campos requeridos"}</small>
        </div>
        {isEditMode && <button className="btn btn-sm btn-outline-secondary" onClick={onCancel}>Cancelar Edición</button>}
      </div>

      <form id="regPacienteForm" onSubmit={handlePreSubmit}>
        <div className="row gx-3 gy-2">
          <div className="col-md-6">
            <label className="form-label fw-bold small mb-1">Nombre Completo</label>
            <input name="nombre" className="form-control form-control-sm" defaultValue={initialData?.nombre} required />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small mb-1">Documento/ID</label>
            <input name="documento" className="form-control form-control-sm" defaultValue={initialData?.documento} required placeholder="CC/TI" />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small mb-1">Sexo</label>
            <select name="sexo" className="form-select form-select-sm" defaultValue={initialData?.sexo} required>
              <option value="">Seleccione...</option>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>

          <div className="col-12"><h6 className="text-secondary mt-2 border-top pt-2 small text-uppercase fw-bold">Ubicación y Contacto</h6></div>

          <div className="col-md-4">
            <label className="form-label fw-bold small mb-1">Municipio</label>
            <select name="municipio" className="form-select form-select-sm" value={municipio} onChange={handleMunicipioChange} required>
              <option value="">Seleccione...</option>
              {municipiosList.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold small mb-1">Zona</label>
            <select name="zona" className="form-select form-select-sm" value={zona} onChange={(e) => setZona(e.target.value)} disabled={!municipio} required>
              <option value="">Seleccione...</option>
              <option value="Urbana">Urbana</option>
              <option value="Rural">Rural</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold small mb-1">Lugar (Barrio/Corregimiento)</label>
            <input
              name="lugar"
              className="form-control form-control-sm"
              value={lugar}
              onChange={(e) => setLugar(e.target.value)}
              placeholder="Barrio..."
              disabled={!zona}
              required
            />
          </div>

          <div className="col-md-8">
            <label className="form-label fw-bold small mb-1">Detalles de Dirección</label>
            <input name="direccion_detalles" className="form-control form-control-sm" placeholder="Calle, Carrera, #Casa (Opcional)" />
          </div>

          <div className="col-md-4">
            <label className="form-label fw-bold small mb-1">Coordenadas (Auto)</label>
            <input value={coords.lat && coords.lng ? `${coords.lat}, ${coords.lng}` : "Pendiente..."} className="form-control form-control-sm bg-light" readOnly />
          </div>

          <div className="col-md-3">
            <label className="form-label fw-bold small mb-1">Fecha Nacimiento</label>
            <input name="fecha_nacimiento" type="date" className="form-control form-control-sm" value={fechaNacimiento} onChange={handleFechaChange} required />
          </div>
          <div className="col-md-2">
            <label className="form-label fw-bold small mb-1">Edad</label>
            <input name="edad" type="number" className="form-control form-control-sm bg-light" value={edad} readOnly placeholder="Auto" />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small mb-1">Teléfono</label>
            <input name="telefono" className="form-control form-control-sm" defaultValue={initialData?.telefono} />
          </div>
          <div className="col-md-3">
            <label className="form-label fw-bold small mb-1">Fecha del Evento (Clínico)</label>
            <input name="fecha_evento" type="date" className="form-control form-control-sm" value={fechaEvento} onChange={(e) => setFechaEvento(e.target.value)} required />
          </div>

          <div className="col-12 mt-3 text-end pt-2 border-top">
            <button type="button" className="btn btn-sm btn-light me-2" onClick={onCancel || (() => setView("home"))}>Cancelar</button>
            <button type="submit" className="btn btn-sm btn-primary px-4 fw-bold">{isEditMode ? "Actualizar" : "Guardar"} Paciente</button>
          </div>
        </div>
      </form>

      {/* Confirmation Modal Overlay */}
      {showConfirm && confirmData && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white d-flex align-items-center justify-content-center" style={{ zIndex: 1050 }}>
          <div className={`card ${patientExists ? 'border-warning' : 'border-primary'}`} style={{ maxWidth: '500px', width: '100%' }}>
            <div className={`card-header ${patientExists ? 'bg-warning text-dark' : 'bg-primary text-white'} py-3`}>
              <h5 className="m-0 fw-bold">
                {patientExists ? (
                  <><i className="bi bi-exclamation-triangle-fill me-2"></i> Paciente ya Registrado</>
                ) : (
                  <><i className="bi bi-check-circle-fill me-2"></i> {isEditMode ? "Confirmar Actualización" : "Confirmar Datos"}</>
                )}
              </h5>
            </div>
            <div className="card-body p-4">
              {patientExists ? (
                <div className="text-center">
                  <p className="mb-4">
                    El documento <strong>{confirmData.documento}</strong> ya pertenece a <strong>{patientExists.nombre}</strong>.
                    <br />No es necesario crear un nuevo registro de paciente.
                  </p>
                  <div className="d-grid gap-2">
                    <button className="btn btn-primary fw-bold" onClick={() => {
                      setCreatedPatient(patientExists);
                      setShowClassification(true);
                      setShowConfirm(false);
                    }}>
                      <i className="bi bi-clipboard-plus me-2"></i> Registrar Nuevo Caso Clínico
                    </button>
                    <button className="btn btn-outline-secondary" onClick={() => setShowConfirm(false)}>
                      Volver al Formulario
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mb-3 text-muted">Por favor verifique que la información es correcta antes de {isEditMode ? "actualizar" : "guardar"}:</p>
                  <ul className="list-group list-group-flush mb-4 small">
                    <li className="list-group-item d-flex justify-content-between"><span>Nombre:</span> <strong>{confirmData.nombre}</strong></li>
                    <li className="list-group-item d-flex justify-content-between"><span>Documento:</span> <strong>{confirmData.documento}</strong></li>
                    <li className="list-group-item d-flex justify-content-between"><span>Municipio:</span> <strong>{confirmData.municipio} ({confirmData.zona})</strong></li>
                    <li className="list-group-item d-flex justify-content-between"><span>Lugar:</span> <strong>{lugar}</strong></li>
                    <li className="list-group-item d-flex justify-content-between"><span>Edad:</span> <strong>{confirmData.edad} años</strong></li>
                    <li className="list-group-item d-flex justify-content-between"><span>Fecha Evento:</span> <strong>{confirmData.fecha_evento}</strong></li>
                  </ul>
                  <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-secondary" onClick={() => setShowConfirm(false)}>Corregir</button>
                    <button className="btn btn-success fw-bold px-4" onClick={handleFinalSubmit}>Confirmar y {isEditMode ? "Actualizar" : "Guardar"}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mandatory Classification Modal */}
      {showClassification && createdPatient && (
        <div className="position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-95 d-flex align-items-center justify-content-center" style={{ zIndex: 1100 }}>
          <div className="card shadow-lg border-0" style={{ maxWidth: '450px', width: '100%', borderRadius: '15px' }}>
            <div className="card-body p-5 text-center">
              <div className="mb-4">
                <i className="bi bi-clipboard2-pulse text-success" style={{ fontSize: '3.5rem' }}></i>
              </div>
              <h4 className="fw-bold mb-2">CLASIFICACIÓN INICIAL</h4>
              <p className="text-muted mb-4">
                Paciente: <strong>{createdPatient.nombre}</strong>
                <br />Por favor, indique el resultado inicial del análisis:
              </p>

              <div className="d-grid gap-3">
                <button
                  className="btn btn-outline-danger btn-lg fw-bold py-3 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => handleClassification('POSITIVO')}
                  disabled={loadingClassification}
                >
                  {loadingClassification ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-plus-circle-fill"></i>}
                  POSITIVO
                </button>

                <button
                  className="btn btn-outline-success btn-lg fw-bold py-3 d-flex align-items-center justify-content-center gap-2"
                  onClick={() => handleClassification('NEGATIVO')}
                  disabled={loadingClassification}
                >
                  {loadingClassification ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-dash-circle-fill"></i>}
                  NEGATIVO
                </button>
              </div>

              <div className="mt-4 pt-3 border-top">
                <small className="text-secondary">
                  <i className="bi bi-info-circle me-1"></i> Los pacientes <strong>Positivos</strong> requieren registro de caso obligatorio.
                </small>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Componente: Buscar Paciente.
 * Interfaz para localizar pacientes antes de registrar un nuevo caso clínico.
 */
const BuscarPaciente = ({ setView, setSelectedPaciente, showToast }) => {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Auto-search if coming from "Mis Pacientes" or just default show recent?
  // Let's standard search
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.get(`${Apiurl}casos/pacientes/`, getAuthHeader());
      const filtered = res.data.filter(p =>
        p.nombre.toLowerCase().includes(search.toLowerCase()) ||
        p.documento.includes(search)
      );
      setResults(filtered);
      if (filtered.length === 0) showToast("No se encontraron pacientes.", "warning");
    } catch (err) {
      console.error(err);
      showToast("Error buscando pacientes.", "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (p) => {
    setSelectedPaciente(p);
    setView("registrarCaso");
  };

  return (
    <div className="table-container">
      <div className="mb-4 pb-2 border-bottom">
        <h5 className="m-0 fw-bold text-primary">Buscar Paciente</h5>
        <small className="text-muted">Seleccione un paciente para registrar un nuevo caso</small>
      </div>

      <form onSubmit={handleSearch} className="d-flex gap-2 mb-4">
        <input
          className="form-control search-input"
          placeholder="Buscar por Nombre o Documento..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button type="submit" className="btn btn-primary d-flex align-items-center gap-2">
          {loading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-search"></i>}
          Buscar
        </button>
      </form>

      {results.length > 0 && (
        <div className="table-responsive mt-3">
          <table className="table table-hover align-middle admin-table">
            <thead>
              <tr>
                <th>Documento</th>
                <th>Nombre</th>
                <th>Edad</th>
                <th>Dirección</th>
                <th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {results.map(p => (
                <tr key={p.id}>
                  <td className="fw-bold">{p.documento}</td>
                  <td>{p.nombre}</td>
                  <td>{p.edad}</td>
                  <td>{p.direccion}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-success" onClick={() => handleSelect(p)}>
                      Seleccionar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/**
 * Componente: Registrar Caso.
 * Formulario técnico para ingresar resultados de laboratorio y nivel de riesgo.
 */
const RegistrarCaso = ({ selectedPaciente, setView, showToast, setSelectedPaciente, initialData, isEditMode, onCancel }) => {
  // If in edit mode, ensure we have a "selectedPaciente" mock
  const effectivePaciente = isEditMode && initialData ? {
    id: initialData.paciente,
    nombre: initialData.paciente_nombre,
    latitud: initialData.latitud,
    longitud: initialData.longitud
  } : selectedPaciente;

  // Auto-fill from patient logic
  const defaultLat = effectivePaciente?.latitud || 8.75;
  const defaultLng = effectivePaciente?.longitud || -75.88;

  const [datos, setDatos] = useState({
    latitud: defaultLat,
    longitud: defaultLng,
    observaciones: "",
    nivel_peligrosidad: "BAJO",
    a_l_res: "NEGATIVO", // Mandatory: POSITIVO or NEGATIVO
    t_t_res: "NEGATIVO",
    unc_res: "NEGATIVO",
    fecha_evento: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (isEditMode && initialData) {
      // Pre-fill from initialData
      const pList = initialData.parasitos_detectados || [];
      setDatos({
        latitud: initialData.latitud || defaultLat,
        longitud: initialData.longitud || defaultLng,
        observaciones: initialData.observaciones || "",
        nivel_peligrosidad: initialData.nivel_peligrosidad || "BAJO",
        a_l_res: pList.includes("Ascaris") ? "POSITIVO" : "NEGATIVO",
        t_t_res: pList.includes("Trichuris") ? "POSITIVO" : "NEGATIVO",
        unc_res: pList.includes("Uncinarias") ? "POSITIVO" : "NEGATIVO",
        fecha_evento: initialData.fecha_evento || new Date().toISOString().split('T')[0]
      });
    } else if (!selectedPaciente) {
      setView("buscarPaciente");
    } else {
      setDatos(d => ({
        ...d,
        latitud: selectedPaciente.latitud || 8.75,
        longitud: selectedPaciente.longitud || -75.88
      }));
    }
  }, [selectedPaciente, setView, isEditMode, initialData, defaultLat, defaultLng]);

  if (!effectivePaciente) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDatos(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      paciente: effectivePaciente.id,
      latitud: parseFloat(datos.latitud),
      longitud: parseFloat(datos.longitud),
      fecha_registro: initialData?.fecha_registro || new Date().toLocaleDateString('en-CA'),
      fecha_evento: datos.fecha_evento,
      observaciones: datos.observaciones,
      nivel_peligrosidad: "BAJO",
      parasitos_detectados: [],
      intensidad_hpg: {}
    };

    if (datos.a_l_res === "POSITIVO") payload.parasitos_detectados.push("Ascaris");
    if (datos.t_t_res === "POSITIVO") payload.parasitos_detectados.push("Trichuris");
    if (datos.unc_res === "POSITIVO") payload.parasitos_detectados.push("Uncinarias");

    if (payload.parasitos_detectados.length >= 2) payload.nivel_peligrosidad = "ALTO";
    else if (payload.parasitos_detectados.length === 1) payload.nivel_peligrosidad = "MEDIO";

    try {
      if (isEditMode && initialData.id) {
        await axios.put(`${Apiurl}casos/${initialData.id}/`, payload, getAuthHeader());
        showToast("✅ Caso actualizado exitosamente.", "success");
      } else {
        await axios.post(`${Apiurl}casos/`, payload, getAuthHeader());
        showToast("✅ Caso registrado exitosamente.", "success");
      }

      setSelectedPaciente(null);
      if (onCancel) onCancel();
      else setView("misCasos");
    } catch (err) {
      console.error('Error registrando caso:', err);
      let msg = "Error registrando caso.";
      if (err.response?.data) {
        msg += " " + JSON.stringify(err.response.data);
      }
      showToast(msg, "danger");
    }
  };

  return (
    <div className="table-container position-relative">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h5 className="m-0 fw-bold text-primary">{isEditMode ? "Editar Caso Clínico" : "Registrar Caso Clínico"}</h5>
          <small className="text-muted">Resultados de laboratorio</small>
        </div>
        <div>
          <span className="badge bg-light text-primary border p-2 me-2">{effectivePaciente.nombre}</span>
          {isEditMode && <button className="btn btn-sm btn-secondary" onClick={onCancel}>Cancelar</button>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-12"><h6 className="text-secondary mt-2 small text-uppercase fw-bold">Ubicación del Caso (Auto-detectada)</h6></div>
          <div className="col-md-6">
            <label className="form-label small fw-bold">Latitud</label>
            <input name="latitud" type="number" step="any" className="form-control" value={datos.latitud} onChange={handleChange} required />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-bold">Longitud</label>
            <input name="longitud" type="number" step="any" className="form-control" value={datos.longitud} onChange={handleChange} required />
          </div>

          <div className="col-md-6">
            <label className="form-label small fw-bold">Fecha del Evento (Día de toma de muestra/reporte)</label>
            <input name="fecha_evento" type="date" className="form-control" value={datos.fecha_evento} onChange={handleChange} required />
          </div>

          <div className="col-12"><h6 className="text-secondary mt-2 small text-uppercase fw-bold">Examen Parasitológico (Resultados)</h6></div>

          <div className="col-md-4">
            <div className={`card p-3 shadow-sm border ${datos.a_l_res === 'POSITIVO' ? 'border-danger bg-danger bg-opacity-10' : 'border-success bg-success bg-opacity-10'}`}>
              <label className="fw-bold mb-2 d-flex align-items-center">
                <ParasiteIcon color="#8D6E63" /><i> Ascaris Lumbricoides</i>
              </label>
              <div className="d-flex gap-3 mt-1">
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="a_l_res" value="POSITIVO" checked={datos.a_l_res === "POSITIVO"} onChange={handleChange} id="a_l_pos" />
                  <label className="form-check-label small fw-bold text-danger" htmlFor="a_l_pos">POSITIVO</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="a_l_res" value="NEGATIVO" checked={datos.a_l_res === "NEGATIVO"} onChange={handleChange} id="a_l_neg" />
                  <label className="form-check-label small fw-bold text-success" htmlFor="a_l_neg">NEGATIVO</label>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className={`card p-3 shadow-sm border ${datos.t_t_res === 'POSITIVO' ? 'border-danger bg-danger bg-opacity-10' : 'border-success bg-success bg-opacity-10'}`}>
              <label className="fw-bold mb-2 d-flex align-items-center">
                <ParasiteIcon color="#D4A373" /> <i>Trichuris Trichiura</i>
              </label>
              <div className="d-flex gap-3 mt-1">
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="t_t_res" value="POSITIVO" checked={datos.t_t_res === "POSITIVO"} onChange={handleChange} id="t_t_pos" />
                  <label className="form-check-label small fw-bold text-danger" htmlFor="t_t_pos">POSITIVO</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="t_t_res" value="NEGATIVO" checked={datos.t_t_res === "NEGATIVO"} onChange={handleChange} id="t_t_neg" />
                  <label className="form-check-label small fw-bold text-success" htmlFor="t_t_neg">NEGATIVO</label>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-4">
            <div className={`card p-3 shadow-sm border ${datos.unc_res === 'POSITIVO' ? 'border-danger bg-danger bg-opacity-10' : 'border-success bg-success bg-opacity-10'}`}>
              <label className="fw-bold mb-2 d-flex align-items-center">
                <ParasiteIcon color="#6B8E23" /> <i>Uncinarias</i>
              </label>
              <div className="d-flex gap-3 mt-1">
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="unc_res" value="POSITIVO" checked={datos.unc_res === "POSITIVO"} onChange={handleChange} id="unc_pos" />
                  <label className="form-check-label small fw-bold text-danger" htmlFor="unc_pos">POSITIVO</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="unc_res" value="NEGATIVO" checked={datos.unc_res === "NEGATIVO"} onChange={handleChange} id="unc_neg" />
                  <label className="form-check-label small fw-bold text-success" htmlFor="unc_neg">NEGATIVO</label>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 mt-3">
            <label className="form-label small fw-bold">Observaciones / Tratamiento</label>
            <textarea name="observaciones" className="form-control" rows="3" value={datos.observaciones} onChange={handleChange}></textarea>
          </div>

          <div className="col-12 mt-4 text-end d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-light" onClick={onCancel || (() => setView("buscarPaciente"))}>Volver</button>
            <button type="submit" className="btn btn-primary px-4">{isEditMode ? "Actualizar" : "Finalizar"}</button>
          </div>
        </div>
      </form>
    </div>
  );
};

/**
 * Componente: Vista de Reporte Anonimizado.
 * Herramienta para la exportación de datos bajo estándares de privacidad.
 */
const AnonymizedReportView = () => {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get Lab Name from User Data
  const userDataStr = localStorage.getItem("userData");
  const userData = userDataStr ? JSON.parse(userDataStr) : {};
  console.log("Debug UserData:", userData); // Para depuración

  const nombreLab =
    (userData.first_name && userData.last_name ? `${userData.first_name} ${userData.last_name}` : "") ||
    userData.first_name ||
    userData.username ||
    userData.email ||
    userData.nombre || // Some backends use this
    "LAB-GENERICO";

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [municipio, setMunicipio] = useState("");
  const [riesgo, setRiesgo] = useState("");
  const [parasito, setParasito] = useState("");
  const [resultado, setResultado] = useState("POSITIVO");

  const [municipiosList, setMunicipiosList] = useState([]);

  useEffect(() => {
    fetchData();
    LocationService.getHierarchy().then(data => {
      setMunicipiosList(Object.keys(data).sort());
    });
  }, []);

  const fetchData = async () => {
    try {
      // Fetch both cases and patients to join data
      const [resCasos, resPacientes] = await Promise.all([
        axios.get(`${Apiurl}casos/`, getAuthHeader()),
        axios.get(`${Apiurl}casos/pacientes/`, getAuthHeader())
      ]);

      const pacientesMap = {};
      resPacientes.data.forEach(p => {
        pacientesMap[p.id] = p;
      });

      // Join data
      const enrichedCasos = resCasos.data.map(c => {
        const pacienteId = typeof c.paciente === 'object' ? c.paciente.id : c.paciente; // Handle if nested or ID
        const pInfo = pacientesMap[pacienteId] || {};

        // Fallback: Parse address if individual fields are missing
        let derivedMuni = pInfo.municipio;
        let derivedZona = pInfo.zona;

        if (pInfo.direccion) {
          // Try specific parsing first
          const parts = pInfo.direccion.split(',').map(s => s.trim());
          if (!derivedMuni && parts.length >= 1) derivedMuni = parts[0];

          // Smart keyword detection for Zona if missing
          if (!derivedZona) {
            const lowerDir = pInfo.direccion.toLowerCase();
            if (lowerDir.includes("urbana") || lowerDir.includes("urbano")) derivedZona = "Urbana";
            else if (lowerDir.includes("rural")) derivedZona = "Rural";
            else if (parts.length >= 2) derivedZona = parts[1]; // Fallback to second part
          }
        }

        return {
          ...c,
          paciente_augmented: pInfo, // Store full patient object
          // Flatten key fields for easier access/filtering if needed, though export logic can use augmented
          municipio_real: c.municipio || derivedMuni || "Desconocido", // Prefer case location, fallback to patient
          zona_real: c.zona || derivedZona || "Desconocida",
          edad_real: pInfo.edad || "N/A",
          sexo_real: pInfo.sexo || "N/A"
        };
      });

      setData(enrichedCasos);
      setFiltered(enrichedCasos);
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let res = data;

    if (startDate) res = res.filter(c => c.fecha_registro >= startDate);
    if (endDate) res = res.filter(c => c.fecha_registro <= endDate);
    if (municipio) res = res.filter(c => c.municipio_real === municipio);
    if (riesgo) res = res.filter(c => c.nivel_peligrosidad === riesgo);
    if (parasito) {
      res = res.filter(c => {
        const isPresent = checkParasitePresence(c.parasitos_detectados, parasito);
        return resultado === "POSITIVO" ? isPresent : !isPresent;
      });
    }

    setFiltered(res);
  }, [data, startDate, endDate, municipio, riesgo, parasito, resultado]);

  // --- Helpers for Export Fields ---
  const getQuarter = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const q = Math.floor((d.getMonth() + 3) / 3);
    return `${d.getFullYear()}-Q${q}`;
  };

  const deriveStatus = (c) => {
    if (!c.fecha_registro) return "Desconocido";
    const daysDiff = (new Date() - new Date(c.fecha_registro)) / (1000 * 60 * 60 * 24);
    return daysDiff < 30 ? 'Activo' : 'Cerrado';
  };

  const cleanParasites = (pList) => {
    if (!pList) return "Ninguno";

    let list = pList;
    if (typeof list === 'string') {
      list = list.trim();
      if (list.startsWith('[') && list.endsWith(']')) {
        try { list = JSON.parse(list.replace(/'/g, '"')); } catch (e) { list = [list]; }
      } else { list = [list]; }
    }

    if (!Array.isArray(list) || list.length === 0) return "Ninguno";

    return list.map(p => {
      const val = String(p);
      if (val.includes("Ascaris")) return "Ascaris lumbricoides";
      if (val.includes("Trichuris")) return "Trichuris trichiura";
      if (val.includes("Uncinaria")) return "Uncinarias";
      return val;
    }).join("; ");
  };

  const processDataForExport = () => {
    return filtered.map(c => ({
      "Código_Caso": `CASE-${c.id}`,
      "Código_Paciente": `PAC-${c.paciente_augmented?.id || c.paciente}`,
      "Fecha_Registro": c.fecha_registro,
      "Edad": c.edad_real,
      "Sexo": c.sexo_real,
      "Municipio": c.municipio_real,
      "Zona": c.zona_real,
      "Parásitos_Detectados": cleanParasites(c.parasitos_detectados),
      "Nivel_Riesgo": c.nivel_peligrosidad || "BAJO",
      "Latitud_Aproximada": c.latitud ? Number(c.latitud).toFixed(3) : "N/A",
      "Longitud_Aproximada": c.longitud ? Number(c.longitud).toFixed(3) : "N/A",
      "Laboratorio_Origen": nombreLab,
      "Periodo_Epidemiológico": getQuarter(c.fecha_registro),
      "Estado_Caso": deriveStatus(c)
    }));
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportCSV = () => {
    const data = processDataForExport();
    if (data.length === 0) return;

    const headers = Object.keys(data[0]).join(",");
    const rows = data.map(row => Object.values(row).map(v => `"${v}"`).join(",")).join("\n");
    const csvContent = "\uFEFF" + headers + "\n" + rows; // Add BOM for Excel UTF-8

    downloadFile(csvContent, `Vigilancia_Lab_${new Date().toISOString().slice(0, 10)}.csv`, "text/csv;charset=utf-8;");
  };

  const handleExportJSON = () => {
    const data = processDataForExport();
    downloadFile(JSON.stringify(data, null, 2), `Vigilancia_Lab_${new Date().toISOString().slice(0, 10)}.json`, "application/json");
  };

  return (
    <div className="container-fluid">
      {/* Privacy Disclaimer */}
      <div className="alert alert-info border-0 shadow-sm d-flex align-items-center mb-4" role="alert">
        <i className="bi bi-shield-lock-fill fs-4 me-3"></i>
        <div>
          <strong>MODO DE PRIVACIDAD ACTIVO</strong>
          <div className="small">Esta vista utiliza identificadores anonimizados (`Código_Paciente`) y coordenadas redondeadas para garantizar la protección de datos personales.</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            {/* Filters - Compact */}
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted">Desde</label>
              <input type="date" className="form-control form-control-sm" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted">Hasta</label>
              <input type="date" className="form-control form-control-sm" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted">Municipio</label>
              <select className="form-select form-select-sm" value={municipio} onChange={e => setMunicipio(e.target.value)}>
                <option value="">Todos</option>
                {municipiosList.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted">Riesgo</label>
              <select className="form-select form-select-sm" value={riesgo} onChange={e => setRiesgo(e.target.value)}>
                <option value="">Todos</option>
                <option value="ALTO">ALTO</option>
                <option value="MEDIO">MEDIO</option>
                <option value="BAJO">BAJO</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted">Parásito</label>
              <select className="form-select form-select-sm" value={parasito} onChange={e => setParasito(e.target.value)}>
                <option value="">Todos</option>
                <option value="Ascaris">Ascaris</option>
                <option value="Trichuris">Trichuris</option>
                <option value="Uncinarias">Uncinarias</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small fw-bold text-muted">Resultado</label>
              <select className="form-select form-select-sm" value={resultado} onChange={e => setResultado(e.target.value)} disabled={!parasito}>
                <option value="POSITIVO">POSITIVO</option>
                <option value="NEGATIVO">NEGATIVO</option>
              </select>
            </div>
            <div className="col-md-12 mt-2">
              <div className="d-flex gap-2 justify-content-end pb-1 border-top pt-2">
                <button className="btn btn-sm btn-outline-success d-flex align-items-center gap-2 px-3 fw-bold" onClick={handleExportCSV}>
                  <i className="bi bi-filetype-csv"></i> Exportar CSV
                </button>
                <button className="btn btn-sm btn-outline-dark d-flex align-items-center gap-2 px-3 fw-bold" onClick={handleExportJSON}>
                  <i className="bi bi-filetype-json"></i> Exportar JSON
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
            <thead className="bg-light">
              <tr>
                <th className="text-uppercase small fw-bold text-muted">Cód. Caso</th>
                <th className="text-uppercase small fw-bold text-muted">Periodo</th>
                <th className="text-uppercase small fw-bold text-muted">Cód. Paciente</th>
                <th className="text-uppercase small fw-bold text-muted">Fecha Reg.</th>
                <th className="text-uppercase small fw-bold text-muted">Edad</th>
                <th className="text-uppercase small fw-bold text-muted">Sexo</th>
                <th className="text-uppercase small fw-bold text-muted">Municipio</th>
                <th className="text-uppercase small fw-bold text-muted">Zona</th>
                <th className="text-uppercase small fw-bold text-muted">Lat (Aprox)</th>
                <th className="text-uppercase small fw-bold text-muted">Lng (Aprox)</th>
                <th className="text-uppercase small fw-bold text-muted">Parásitos Detectados</th>
                <th className="text-uppercase small fw-bold text-muted">Riesgo</th>
                <th className="text-uppercase small fw-bold text-muted">Estado</th>
                <th className="text-uppercase small fw-bold text-muted">Lab. Origen</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="14" className="text-center py-5">Cargando registros...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="14" className="text-center py-5 text-muted">No se encontraron registros.</td></tr>
              ) : (
                filtered.map(c => (
                  <tr key={c.id}>
                    <td><code className="text-primary fw-bold">CASE-{c.id}</code></td>
                    <td>{getQuarter(c.fecha_registro)}</td>
                    <td><span className="badge bg-light text-dark border">PAC-{c.paciente_augmented?.id || c.paciente}</span></td>
                    <td>{c.fecha_registro}</td>
                    <td>{c.edad_real}</td>
                    <td>{c.sexo_real}</td>
                    <td>{c.municipio_real}</td>
                    <td>{c.zona_real}</td>
                    <td className="font-monospace text-muted">{c.latitud ? Number(c.latitud).toFixed(3) : "N/A"}</td>
                    <td className="font-monospace text-muted">{c.longitud ? Number(c.longitud).toFixed(3) : "N/A"}</td>
                    <td className="text-wrap" style={{ maxWidth: '250px' }}>{cleanParasites(c.parasitos_detectados)}</td>
                    <td>
                      <span className={`badge ${c.nivel_peligrosidad === 'ALTO' ? 'bg-danger' : c.nivel_peligrosidad === 'MEDIO' ? 'bg-warning text-dark' : 'bg-success'}`}>
                        {c.nivel_peligrosidad || 'BAJO'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${deriveStatus(c) === 'Activo' ? 'bg-info text-dark' : 'bg-secondary'}`}>
                        {deriveStatus(c)}
                      </span>
                    </td>
                    <td className="text-muted small">{nombreLab}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white text-end text-muted small">
          Registros visibles: {filtered.length} | Total Histórico: {data.length}
        </div>
      </div>
    </div>
  );
};

// --- Main Component ---

/**
 * Componente Principal: Dashboard Laboratorio.
 * Orquestador de vistas y estado global del panel de laboratorio.
 */
export default function DashboardLab() {
  const [view, setView] = useState("home");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [initialCaseSearch, setInitialCaseSearch] = useState("");

  // Styles are now handled by LabLayout importing 'DashboardAdmin.css' whi
  const [editingPatient, setEditingPatient] = useState(null);
  const [editingCase, setEditingCase] = useState(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type }), 3500);
  }, []);

  const handleEditPatient = (patient) => {
    setEditingPatient(patient);
    setView("registrarPaciente");
  };

  const handleEditCase = (caseItem) => {
    setEditingCase(caseItem);
    setView("registrarCaso");
  };

  const handleViewHistory = (paciente) => {
    setInitialCaseSearch(paciente.documento);
    setView("misCasos");
  };

  let title = "Gestión de Laboratorio";
  let breadcrumb = "Laboratorio / Inicio";

  switch (view) {
    case 'home': title = "Resumen Ejecutivo"; break;
    case 'registrarPaciente':
      title = editingPatient ? "Editar Paciente" : "Registrar Nuevo Paciente";
      breadcrumb = editingPatient ? "Laboratorio / Pacientes / Editar" : "Laboratorio / Pacientes / Nuevo";
      break;
    case 'misPacientes': title = "Mis Pacientes"; breadcrumb = "Laboratorio / Pacientes"; break;
    case 'buscarPaciente': title = "Buscar Paciente"; breadcrumb = "Laboratorio / Casos / Buscar"; break;
    case 'registrarCaso':
      title = editingCase ? "Editar Caso Clínico" : "Registrar Caso";
      breadcrumb = editingCase ? "Laboratorio / Casos / Editar" : "Laboratorio / Casos / Registrar";
      break;
    case 'misCasos': title = "Mis Casos"; breadcrumb = "Laboratorio / Casos"; break;
    case 'reporteAnonimizado': title = "Reporte Anonimizado (Exportar)"; breadcrumb = "Laboratorio / Datos Anonimizados"; break;
  }

  const handleNav = (v) => {
    setInitialCaseSearch("");
    setView(v);
  };

  return (
    <LabLayout currentView={view} setView={handleNav} title={title} breadcrumb={breadcrumb}>
      <div className="toast-container position-fixed top-0 end-0 p-3" style={{ zIndex: 1055 }}>
        <div className={`toast align-items-center text-bg-${toast.type} border-0 ${toast.show ? 'show' : ''}`} role="alert">
          <div className="d-flex">
            <div className="toast-body">{toast.message}</div>
            <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast({ ...toast, show: false })}></button>
          </div>
        </div>
      </div>

      {view === 'home' && <ExecutiveSummary setView={setView} />}
      {view === 'registrarPaciente' &&
        <RegistrarPaciente
          showToast={showToast}
          setView={setView}
          initialData={editingPatient}
          isEditMode={!!editingPatient}
          setSelectedPaciente={setSelectedPaciente}
          onCancel={() => {
            setEditingPatient(null);
            setView("misPacientes");
          }}
        />
      }
      {view === 'misPacientes' && <MisPacientes onViewDetails={(p) => console.log(p)} onEdit={handleEditPatient} onViewHistory={handleViewHistory} showToast={showToast} />}
      {view === 'buscarPaciente' && <BuscarPaciente setView={setView} setSelectedPaciente={setSelectedPaciente} showToast={showToast} />}
      {view === 'registrarCaso' &&
        <RegistrarCaso
          selectedPaciente={selectedPaciente}
          setSelectedPaciente={setSelectedPaciente}
          setView={setView}
          showToast={showToast}
          initialData={editingCase}
          isEditMode={!!editingCase}
          onCancel={() => {
            setEditingCase(null);
            setView("misCasos");
          }}
        />
      }
      {view === 'misCasos' && (
        <MisCasos
          onEdit={handleEditCase}
          showToast={showToast}
          initialSearch={initialCaseSearch}
        />
      )}
      {view === 'reporteAnonimizado' && <AnonymizedReportView />}
    </LabLayout>
  );
};
